import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subscriptionPlanService, SubscriptionPlan } from '../../../services/subscriptionPlanService';
import SubscriptionPlansTable from '../components/SubscriptionPlansTable';
import CreateEditPlanModal from '../components/CreateEditPlanModal';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { showToast } from '../../../components/Toast';

const SubscriptionPlansPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch admin plans using TanStack Query
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptionPlans', { page, limit, status }],
    queryFn: () => subscriptionPlanService.getPlans(page, limit, status),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus as 'all' | 'active' | 'inactive');
    setPage(1);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setIsModalOpen(true);
  };

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  const handleToggleStatus = async (planId: string, currentStatus: boolean) => {
    try {
      setIsSubmitting(true);
      await subscriptionPlanService.togglePlanStatus(planId, !currentStatus);
      refetch();
    } catch (err) {
      console.error('Error toggling plan status:', err);
      showToast('Failed to update plan status', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);
      if (selectedPlan) {
        // Edit mode
        await subscriptionPlanService.updatePlan(selectedPlan.id, formData);
      } else {
        // Create mode
        await subscriptionPlanService.createPlan(formData);
      }
      handleCloseModal();
      refetch();
    } catch (err: any) {
      console.error('Error saving plan:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save plan';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Combine and filter all plans
  const adminPlans = data?.data || [];
  const allPlans = adminPlans;
  
  const filteredPlans = allPlans.filter(plan =>
    plan.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (error) {
    console.error('Failed to load subscription plans:', error);
  }

  return (
    <div className="min-h-screen bg-[#09140F] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Subscription Plans</h1>
          <p className="text-gray-400">Manage all subscription plans for your gym</p>
        </div>

        {/* Filters and Actions */}
        <div className="bg-[#16251D] border border-gray-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-end justify-between">
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={status}
                    onChange={e => handleStatusChange(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#23F47C] transition-colors"
                  >
                    <option value="all">All Plans</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Search Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Search by Name</label>
                  <input
                    type="text"
                    placeholder="Search plans..."
                    value={searchTerm}
                    onChange={e => handleSearchChange(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#23F47C] transition-colors placeholder-gray-500"
                  />
                </div>
              </div>
            </div>

            {/* Create Plan Button */}
            <button
              onClick={handleCreatePlan}
              className="inline-flex items-center gap-2 bg-[#23F47C] text-gray-900 px-6 py-2 rounded-lg font-medium hover:bg-[#1dd45f] transition-colors whitespace-nowrap"
            >
              <Plus size={20} />
              Create Plan
            </button>
          </div>
        </div>

        {/* All Plans Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-white mb-1">All Plans ({allPlans.length})</h2>
            <p className="text-gray-400 text-sm">System plans and custom plans you've created</p>
          </div>
          <div className="bg-[#16251D] border border-gray-800 rounded-lg overflow-hidden">
            {error && (
              <div className="p-6 text-center text-red-400">
                Failed to load subscription plans. Please try again.
              </div>
            )}
            <SubscriptionPlansTable
              plans={filteredPlans}
              isLoading={isLoading}
              error={error ? 'Failed to load plans' : undefined}
              onEdit={handleEditPlan}
              onToggleStatus={handleToggleStatus}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
            <div className="text-gray-400 text-sm">
              Showing {((page || 1) - 1) * (limit || 10) + 1} to{' '}
              {Math.min((page || 1) * (limit || 10), data.total)} of {data.total}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange((page || 1) - 1)}
                disabled={(page || 1) <= 1}
                className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex gap-1">
                {(() => {
                  const current = page || 1;
                  const total = data.totalPages;
                  const pages: number[] = [];
                  if (total <= 7) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (current > 3) pages.push(-1); // ellipsis
                    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
                    if (current < total - 2) pages.push(-2); // ellipsis
                    pages.push(total);
                  }
                  return pages.map((p, idx) =>
                    p < 0 ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-2 text-gray-500">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          current === p
                            ? 'bg-[#23F47C] text-gray-900'
                            : 'border border-gray-700 text-gray-400 hover:bg-gray-800'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  );
                })()}
              </div>

              <button
                onClick={() => handlePageChange((page || 1) + 1)}
                disabled={(page || 1) >= data.totalPages}
                className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-gray-400 text-sm">Items per page:</label>
              <select
                value={limit || 10}
                onChange={e => handleLimitChange(parseInt(e.target.value))}
                className="bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#23F47C]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <CreateEditPlanModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        plan={selectedPlan}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default SubscriptionPlansPage;
